import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowDown, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const HeroSection = () => {
  const [showHindi, setShowHindi] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Auto-switch description between English and Hindi every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShowHindi(prev => !prev);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  const descriptionEnglish = "Like a camel that thrives in the harshest desert conditions, our rubber bush is engineered to withstand extreme heat and sand-laden water, ensuring your submersible pumps run efficiently in any condition.";
  
  const descriptionHindi = "जैसे ऊंट रेगिस्तान की कठोर परिस्थितियों में जीवित रहता है, वैसे ही हमारा रबर बुश अत्यधिक गर्मी और रेत युक्त पानी का सामना करने के लिए तैयार किया गया है, जो आपके सबमर्सिबल पंप को किसी भी स्थिति में कुशलता से चलाता है।";

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDownloadBrochure = async () => {
    try {
      setIsDownloading(true);
      // Fetch the PDF as a blob to force download instead of opening in browser
      const response = await fetch('/Camel_Brochure.pdf');
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Camel Brochure.pdf';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsDownloading(false);
    } catch (error) {
      console.error('Error downloading brochure:', error);
      setIsDownloading(false);
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1489493585363-d69421e0edd3)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 65%',
          backgroundAttachment: 'scroll'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-amber-900/70"></div>
      </div>
      
      <style jsx>{`
        @media (min-width: 768px) {
          .absolute.inset-0.z-0 {
            background-attachment: fixed !important;
            background-position: center 60% !important;
          }
        }
        @media (max-width: 767px) {
          .absolute.inset-0.z-0 {
            background-position: center 70% !important;
          }
        }
      `}</style>

      {/* Content */}
      <div className="container mx-auto px-4 z-10 text-center">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Main Heading with Custom Font */}
          <h1 className="text-5xl sm:text-6xl md:text-8xl leading-tight" style={{ fontFamily: "'Monotype Corsiva', cursive", fontWeight: 'normal' }}>
            <span className="text-white" style={{ textShadow: '3px 3px 8px rgba(0,0,0,0.9)' }}>Camel</span>
            <span className="block text-amber-400 mt-2" style={{ textShadow: '3px 3px 8px rgba(0,0,0,0.9)' }}>Rubber Bush</span>
          </h1>

          {/* Tagline */}
          <div className="space-y-2 sm:space-y-3">
            <p className="text-lg sm:text-xl md:text-3xl text-amber-200 font-semibold italic px-4">
              "Unyielding in Heat, Unstoppable in Sand"
            </p>
            <p className="text-base sm:text-lg md:text-2xl text-amber-100 font-medium px-4">
              "धूप में ढलता नहीं, रेत में रुकता नहीं"
            </p>
          </div>

          {/* Auto-switching Description */}
          <p className="text-sm sm:text-base md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed min-h-[80px] sm:min-h-[100px] flex items-center justify-center transition-opacity duration-500 px-6">
            {showHindi ? descriptionHindi : descriptionEnglish}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-4 sm:pt-6 px-4">
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Get Quote Now
            </Button>
            <Button 
              onClick={handleDownloadBrochure}
              size="lg"
              variant="outline"
              disabled={isDownloading}
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-amber-800 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-lg shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="mr-2" size={20} />
              {isDownloading ? 'Downloading...' : 'Download Brochure'}
            </Button>
          </div>

          {/* Scroll Indicator */}
          <div className="pt-12 animate-bounce">
            <ArrowDown className="mx-auto text-amber-400" size={32} />
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-amber-50 to-transparent z-10"></div>
    </section>
  );
};

export default HeroSection;
